import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Upload,
  message,
  Radio,
  Row,
  Col,
  InputNumber,
  Switch,
  Card,
  Tag,
  Tooltip,
  Spin
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  UploadOutlined,
  BgColorsOutlined,
  AppstoreOutlined,
  ShoppingOutlined,
  EyeOutlined,
  PictureOutlined,
  CloseCircleOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import LifestyleServ from "../../service/LifestyleServ";
import CategoryServ from "../../service/CategoryServ";
import ProductServ from "../../service/ProductServ";
import SafeImage, { getImageUrl } from "../../component/SafeImage";
import Breadcrumbs from "../../component/Breadcrumbs";
import CuratedProductPickerModal from "../../component/CuratedProductPickerModal";

const { Option } = Select;

// Quick Palette Presets
const COLOR_PRESETS = [
  {
    name: "Tech Blue",
    bg: "#EFF6FF",
    titleColor: "#003366",
    subColor: "#336699",
    btnBg: "#0055FF",
    borderColor: "#DBEAFE",
  },
  {
    name: "Warm Sunset",
    bg: "#FFF7ED",
    titleColor: "#7A1400",
    subColor: "#8A3C24",
    btnBg: "#FF5200",
    borderColor: "#FED7AA",
  },
  {
    name: "Fresh Green",
    bg: "#F0FDF4",
    titleColor: "#0B4F2C",
    subColor: "#2E7D52",
    btnBg: "#16A34A",
    borderColor: "#DCFCE7",
  },
  {
    name: "Royal Purple",
    bg: "#FAF5FF",
    titleColor: "#5C0D26",
    subColor: "#8A2E4B",
    btnBg: "#9D174D",
    borderColor: "#E9D5FF",
  },
  {
    name: "Cozy Amber",
    bg: "#FFFBEB",
    titleColor: "#78350F",
    subColor: "#B45309",
    btnBg: "#D97706",
    borderColor: "#FDE68A",
  },
  {
    name: "Vibrant Rose",
    bg: "#FFF1F2",
    titleColor: "#881337",
    subColor: "#9F1239",
    btnBg: "#E11D48",
    borderColor: "#FECDD3",
  },
];

const LifestyleForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  const watchedCategories = Form.useWatch('categories', form) || [];

  const [imagePreview, setImagePreview] = useState("");
  const [imageError, setImageError] = useState("");
  const [inputType, setInputType] = useState("upload");
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Live real-time preview watch state
  const [previewValues, setPreviewValues] = useState({
    title: "Office Setup",
    subtitle: "Work Better\nFrom Home",
    bg: "#EFF6FF",
    titleColor: "#003366",
    subColor: "#336699",
    btnBg: "#0055FF",
    borderColor: "#DBEAFE",
    imageUrl: "",
    type: "both",
    categories: [],
    products: [],
  });

  // Fetch Category and Product dropdown options
  useEffect(() => {
    const fetchSelectOptions = async () => {
      try {
        setLoadingOptions(true);
        const [catRes, prodRes] = await Promise.all([
          CategoryServ.index({ limit: 500, filter: JSON.stringify({ isLive: "all" }) }),
          ProductServ.index({ limit: 500 }),
        ]);

        const catData = Array.isArray(catRes?.data) ? catRes.data : (Array.isArray(catRes) ? catRes : []);
        const prodData = Array.isArray(prodRes?.data) ? prodRes.data : (Array.isArray(prodRes) ? prodRes : []);

        setCategories(prev => {
          const map = new Map();
          prev.forEach(c => map.set(String(c._id || c.id), c));
          catData.forEach(c => map.set(String(c._id || c.id), c));
          return Array.from(map.values());
        });

        setProducts(prev => {
          const map = new Map();
          prev.forEach(p => map.set(String(p._id || p.id), p));
          prodData.forEach(p => map.set(String(p._id || p.id), p));
          return Array.from(map.values());
        });
      } catch (err) {
        console.error("Failed to load category / product options", err);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchSelectOptions();
  }, []);

  // Fetch details if editing or reset if creating
  useEffect(() => {
    if (isEditing) {
      const fetchDetails = async () => {
        try {
          setFetching(true);
          const res = await LifestyleServ.single(id);
          const data = res?.data || res;
          if (data) {
            // Merge any populated categories from data into categories state if missing
            const loadedCats = (data.categories || []).concat(data.category ? [data.category] : []).filter(Boolean);
            const catObjs = loadedCats.filter(c => typeof c === "object" && (c._id || c.id) && c.name);
            if (catObjs.length > 0) {
              setCategories(prev => {
                const map = new Map(prev.map(c => [String(c._id || c.id), c]));
                catObjs.forEach(c => map.set(String(c._id || c.id), c));
                return Array.from(map.values());
              });
            }

            // Merge any populated products from data into products state if missing
            const loadedProds = (data.products || []).filter(Boolean);
            const prodObjs = loadedProds.filter(p => typeof p === "object" && (p._id || p.id) && p.name);
            if (prodObjs.length > 0) {
              setProducts(prev => {
                const map = new Map(prev.map(p => [String(p._id || p.id), p]));
                prodObjs.forEach(p => map.set(String(p._id || p.id), p));
                return Array.from(map.values());
              });
            }

            // Extract valid IDs
            const catIds = loadedCats
              .map(c => (typeof c === "object" ? (c._id || c.id) : c))
              .filter(Boolean)
              .map(String);

            const prodIds = loadedProds
              .map(p => (typeof p === "object" ? (p._id || p.id) : p))
              .filter(Boolean)
              .map(String);

            setSelectedProductIds(prodIds);

            const initialValues = {
              title: data.title || "",
              subtitle: data.subtitle || "",
              type: data.type || "both",
              categories: catIds,
              products: prodIds,
              bg: data.bg || "#EFF6FF",
              titleColor: data.titleColor || "#003366",
              subColor: data.subColor || "#336699",
              btnBg: data.btnBg || "#0055FF",
              borderColor: data.borderColor || "#DBEAFE",
              imageUrl: data.imageUrl || "",
              priority: data.priority !== undefined ? data.priority : 1,
              isActive: data.isActive !== false && data.isLive !== false,
            };

            form.setFieldsValue(initialValues);
            setImagePreview(data.imageUrl || "");
            setPreviewValues({
              ...initialValues,
              imageUrl: data.imageUrl || "",
            });

            if (data.imageUrl && (data.imageUrl.startsWith("http://") || data.imageUrl.startsWith("https://"))) {
              setInputType("url");
            } else {
              setInputType("upload");
            }
          }
        } catch (err) {
          console.error("Failed to load lifestyle details:", err);
          message.error("Failed to load collection details");
        } finally {
          setFetching(false);
        }
      };

      fetchDetails();
    } else {
      const defaults = {
        title: "",
        subtitle: "",
        type: "both",
        categories: [],
        products: [],
        bg: "#EFF6FF",
        titleColor: "#003366",
        subColor: "#336699",
        btnBg: "#0055FF",
        borderColor: "#DBEAFE",
        imageUrl: "",
        priority: 1,
        isActive: true,
      };
      setSelectedProductIds([]);
      form.resetFields();
      form.setFieldsValue(defaults);
      setImagePreview("");
      setInputType("upload");
      setPreviewValues(defaults);
    }
  }, [isEditing, id, form]);

  const handleValuesChange = (_, allValues) => {
    setPreviewValues(prev => ({
      ...prev,
      ...allValues,
      products: selectedProductIds,
      imageUrl: imagePreview || allValues.imageUrl || "",
    }));
  };

  const handleRemoveProduct = (prodId) => {
    const updated = selectedProductIds.filter(id => String(id) !== String(prodId));
    setSelectedProductIds(updated);
    form.setFieldsValue({ products: updated });
    setPreviewValues(prev => ({ ...prev, products: updated }));
  };

  const handleSavePickedProducts = (newIds, newObjs) => {
    const stringIds = (newIds || []).map(String);
    setSelectedProductIds(stringIds);
    form.setFieldsValue({ products: stringIds });
    setPreviewValues(prev => ({ ...prev, products: stringIds }));
    if (newObjs && newObjs.length > 0) {
      setProducts(prev => {
        const map = new Map(prev.map(p => [String(p._id || p.id), p]));
        newObjs.forEach(p => map.set(String(p._id || p.id), p));
        return Array.from(map.values());
      });
    }
    message.success(`${stringIds.length} product(s) attached to collection`);
  };

  const applyPreset = (preset) => {
    form.setFieldsValue({
      bg: preset.bg,
      titleColor: preset.titleColor,
      subColor: preset.subColor,
      btnBg: preset.btnBg,
      borderColor: preset.borderColor,
    });
    setPreviewValues(prev => ({
      ...prev,
      bg: preset.bg,
      titleColor: preset.titleColor,
      subColor: preset.subColor,
      btnBg: preset.btnBg,
      borderColor: preset.borderColor,
    }));
    message.info(`Applied "${preset.name}" color theme`);
  };

  const handleImageUpload = async (file) => {
    try {
      setIsUploadingImage(true);
      setImageError("");
      const formData = new FormData();
      formData.append("image", file);
      const res = await LifestyleServ.uploadImage(formData);
      if (res && res.imageUrl) {
        const fullUrl = getImageUrl(res.imageUrl);
        setImagePreview(fullUrl);
        form.setFieldsValue({ imageUrl: res.imageUrl });
        setPreviewValues(prev => ({ ...prev, imageUrl: fullUrl }));
        message.success("Image uploaded successfully");
      }
    } catch (error) {
      const errorMsg = error.message || "Failed to upload image";
      setImageError(errorMsg);
      message.error(errorMsg);
    } finally {
      setIsUploadingImage(false);
    }
    return false;
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const payload = {
        title: values.title?.trim(),
        subtitle: values.subtitle?.trim() || "",
        type: values.type || "both",
        categories: values.categories || [],
        products: selectedProductIds,
        bg: values.bg || "#EFF6FF",
        titleColor: values.titleColor || "#003366",
        subColor: values.subColor || "#336699",
        btnBg: values.btnBg || "#0055FF",
        borderColor: values.borderColor || "#DBEAFE",
        imageUrl: imagePreview || values.imageUrl || "",
        priority: Number(values.priority) || 1,
        isActive: values.isActive !== false,
        isLive: values.isActive !== false,
      };

      if (!payload.title) {
        message.error("Please enter collection title");
        return;
      }

      if (isEditing) {
        await LifestyleServ.update(id, payload);
        message.success("Lifestyle & Need collection updated successfully!");
      } else {
        await LifestyleServ.create(payload);
        message.success("Lifestyle & Need collection created successfully!");
      }

      navigate("/admin/lifestyles");
    } catch (err) {
      console.error("Save error:", err);
      message.error(err.response?.data?.message || err.message || "Failed to save collection");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div style={{ padding: "60px 0", textAlign: "center" }}>
        <Spin size="large" tip="Loading collection details..." />
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 24px", background: "#f8fafc", minHeight: "100vh" }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          { label: "Admin", link: "/admin/dashboard" },
          { label: "Product Management" },
          { label: "Lifestyle & Needs", link: "/admin/lifestyles" },
          { label: isEditing ? "Edit Collection" : "Create Collection" }
        ]}
      />

      {/* Header Bar */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
        marginBottom: 20
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Tooltip title="Back to Lifestyle & Needs List">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/admin/lifestyles")}
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                color: "#0f172a",
                fontSize: 16,
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
              }}
            />
          </Tooltip>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{
                background: "linear-gradient(135deg, #0055FF 0%, #7A1400 100%)",
                color: "#fff",
                width: 34,
                height: 34,
                borderRadius: 8,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18
              }}>
                ✨
              </span>
              {isEditing ? "Edit Lifestyle & Need Collection" : "Create New Lifestyle / Need Collection"}
            </h2>
            <p style={{ color: "#64748b", margin: "4px 0 0 0", fontSize: 13 }}>
              Curate multi-category bundles, custom color palettes, and featured products for customer discovery
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
          <Button
            onClick={() => navigate("/admin/lifestyles")}
            style={{ height: 38, borderRadius: 8, padding: "0 16px" }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={loading}
            style={{
              background: "linear-gradient(135deg, #0055FF 0%, #0040C0 100%)",
              borderColor: "#0055FF",
              fontWeight: 700,
              height: 38,
              padding: "0 22px",
              borderRadius: 8
            }}
          >
            {isEditing ? "Update Collection" : "Save Collection"}
          </Button>
        </div>
      </div>

      {/* Main Content Form Grid */}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={handleValuesChange}
        initialValues={{
          type: "both",
          priority: 1,
          isActive: true,
          bg: "#EFF6FF",
          titleColor: "#003366",
          subColor: "#336699",
          btnBg: "#0055FF",
          borderColor: "#DBEAFE",
        }}
      >
        <Row gutter={[24, 24]}>
          {/* Left Column: Form Sections */}
          <Col xs={24} lg={15}>
            {/* 1. Collection Information */}
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                  <AppstoreOutlined style={{ color: "#0055FF" }} /> 1. Collection Details
                </div>
              }
              style={{ borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 20 }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={16}>
                  <Form.Item
                    name="title"
                    label={<span style={{ fontWeight: 600 }}>Collection Title *</span>}
                    rules={[{ required: true, message: "Please enter title" }]}
                    extra={<span style={{ fontSize: 11, color: "#94a3b8" }}>E.g. "Office Setup" or "Work From Home"</span>}
                  >
                    <Input placeholder="Enter title..." size="large" style={{ borderRadius: 8 }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="priority"
                    label={<span style={{ fontWeight: 600 }}>Sort Priority</span>}
                    extra={<span style={{ fontSize: 11, color: "#94a3b8" }}>1 = First on Home</span>}
                  >
                    <InputNumber min={1} max={999} size="large" style={{ width: "100%", borderRadius: 8 }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="subtitle"
                label={<span style={{ fontWeight: 600 }}>Subtitle / Tagline</span>}
                extra={<span style={{ fontSize: 11, color: "#94a3b8" }}>E.g. "Work Better From Home" (Supports multi-line)</span>}
              >
                <Input placeholder="Enter subtitle..." size="large" style={{ borderRadius: 8 }} />
              </Form.Item>

              <Form.Item
                name="type"
                label={<span style={{ fontWeight: 600 }}>Display Section</span>}
                rules={[{ required: true }]}
              >
                <Radio.Group buttonStyle="solid" style={{ width: "100%" }} size="large">
                  <Radio.Button value="both" style={{ width: "33.3%", textAlign: "center" }}>
                    🔥 Both Sections
                  </Radio.Button>
                  <Radio.Button value="lifestyle" style={{ width: "33.3%", textAlign: "center" }}>
                    ✨ Shop by Lifestyle
                  </Radio.Button>
                  <Radio.Button value="need" style={{ width: "33.3%", textAlign: "center" }}>
                    🎯 Shop by Need
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Card>

            {/* 2. Linked Categories & Products */}
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                  <ShoppingOutlined style={{ color: "#16A34A" }} /> 2. Linked Categories & Curated Products
                </div>
              }
              style={{ borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 20 }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Form.Item
                name="categories"
                label={<span style={{ fontWeight: 600 }}>Linked Categories (Multi-Select)</span>}
                extra={<span style={{ fontSize: 12, color: "#64748b" }}>Select all categories included in this lifestyle (e.g. Furniture + Electronics for Office Setup)</span>}
              >
                <Select
                  mode="multiple"
                  placeholder="Select relevant categories..."
                  loading={loadingOptions}
                  allowClear
                  showSearch
                  size="large"
                  filterOption={(input, option) =>
                    (option?.children || "").toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: "100%", borderRadius: 8 }}
                >
                  {categories.map(cat => (
                    <Option key={String(cat._id || cat.id)} value={String(cat._id || cat.id)}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Hidden Form.Item for products */}
              <Form.Item name="products" hidden>
                <Input />
              </Form.Item>

              {/* Curated Products Section with Dedicated Visual Picker */}
              <div style={{ marginTop: 24, borderTop: "1px solid #f1f5f9", paddingTop: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                      <span>Curated Products (Optional Specific Items)</span>
                      <Tag color={selectedProductIds.length > 0 ? "blue" : "default"} style={{ fontWeight: 700, borderRadius: 10 }}>
                        {selectedProductIds.length} attached
                      </Tag>
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                      {watchedCategories.length > 0 ? (
                        <span>Pick specific products to highlight from your selected categories ({watchedCategories.length} categories active)</span>
                      ) : (
                        <span>Select categories above first, then click below to pick products</span>
                      )}
                    </div>
                  </div>

                  <Button
                    type="primary"
                    icon={<ShoppingOutlined />}
                    onClick={() => setPickerOpen(true)}
                    style={{
                      background: "linear-gradient(135deg, #0055FF 0%, #0040C0 100%)",
                      borderColor: "#0055FF",
                      fontWeight: 700,
                      borderRadius: 8,
                      height: 36,
                      boxShadow: "0 2px 6px rgba(0,85,255,0.2)"
                    }}
                  >
                    + Pick Products from Categories ({selectedProductIds.length})
                  </Button>
                </div>

                {/* Attached Products Visual Grid */}
                {selectedProductIds.length > 0 ? (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: 12,
                    background: "#f8fafc",
                    padding: 14,
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    maxHeight: "380px",
                    overflowY: "auto"
                  }}>
                    {selectedProductIds.map(id => {
                      const prod = products.find(p => String(p._id || p.id) === String(id));
                      if (!prod) {
                        return (
                          <div key={id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#fff", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                            <span style={{ fontSize: 12, color: "#64748b" }}>Product #{String(id).slice(-6)}</span>
                            <Button type="text" size="small" danger onClick={() => handleRemoveProduct(id)} icon={<DeleteOutlined />} />
                          </div>
                        );
                      }

                      const image = prod.imageUrl || prod.images?.[0];
                      const price = prod.price || prod.mrp || 0;
                      const categoryName = prod.categoryId?.name || prod.category?.name;
                      const shopName = prod.shopId?.name || prod.shop?.name;

                      return (
                        <div
                          key={id}
                          style={{
                            display: "flex",
                            gap: 10,
                            padding: 10,
                            background: "#ffffff",
                            borderRadius: 10,
                            border: "1px solid #cbd5e1",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
                            position: "relative",
                            alignItems: "center"
                          }}
                        >
                          <div style={{ width: 48, height: 48, borderRadius: 6, overflow: "hidden", background: "#f1f5f9", flexShrink: 0, border: "1px solid #e2e8f0" }}>
                            <SafeImage src={getImageUrl(image)} alt={prod.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={prod.name}>
                              {prod.name}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "2px 0" }}>
                              {categoryName && <Tag color="blue" style={{ fontSize: 9, padding: "0 4px", margin: 0, borderRadius: 3 }}>{categoryName}</Tag>}
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#0055FF" }}>₹{price}</span>
                            </div>
                            {shopName && (
                              <div style={{ fontSize: 10, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                🏬 {shopName}
                              </div>
                            )}
                          </div>
                          <Tooltip title="Remove product">
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<CloseCircleOutlined style={{ fontSize: 16 }} />}
                              onClick={() => handleRemoveProduct(id)}
                              style={{ padding: 4 }}
                            />
                          </Tooltip>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    onClick={() => setPickerOpen(true)}
                    style={{
                      border: "2px dashed #cbd5e1",
                      padding: "26px 16px",
                      textAlign: "center",
                      borderRadius: 12,
                      cursor: "pointer",
                      background: "#f8fafc",
                      transition: "all 0.2s ease"
                    }}
                  >
                    <ShoppingOutlined style={{ fontSize: 32, color: "#0055FF", marginBottom: 8 }} />
                    <div style={{ fontWeight: 700, color: "#334155", fontSize: 14 }}>
                      No curated products attached yet
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                      Click here to open the product picker with images, pricing, and category filters
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* 3. Theme & Color Customization */}
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                  <BgColorsOutlined style={{ color: "#FF5200" }} /> 3. Card Theme & Colors
                </div>
              }
              style={{ borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 20 }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              {/* Quick Preset Buttons */}
              <div style={{ marginBottom: 18 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#475569", display: "block", marginBottom: 8 }}>
                  1-Click Theme Presets:
                </span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {COLOR_PRESETS.map((preset, idx) => (
                    <Button
                      key={idx}
                      onClick={() => applyPreset(preset)}
                      style={{
                        background: preset.bg,
                        borderColor: preset.borderColor,
                        color: preset.titleColor,
                        fontWeight: 700,
                        fontSize: 12,
                        borderRadius: 8,
                        height: 34
                      }}
                    >
                      <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: preset.btnBg, marginRight: 6 }} />
                      {preset.name}
                    </Button>
                  ))}
                </div>
              </div>

              <Row gutter={12}>
                <Col xs={24} sm={12}>
                  <Form.Item name="bg" label={<span style={{ fontWeight: 600 }}>Card Background Color</span>}>
                    <Input
                      type="color"
                      style={{ width: "100%", height: 42, cursor: "pointer", padding: "2px 6px", borderRadius: 8 }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="borderColor" label={<span style={{ fontWeight: 600 }}>Card Border Color</span>}>
                    <Input
                      type="color"
                      style={{ width: "100%", height: 42, cursor: "pointer", padding: "2px 6px", borderRadius: 8 }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item name="titleColor" label={<span style={{ fontWeight: 600 }}>Title Text Color</span>}>
                    <Input
                      type="color"
                      style={{ width: "100%", height: 42, cursor: "pointer", padding: "2px 6px", borderRadius: 8 }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item name="subColor" label={<span style={{ fontWeight: 600 }}>Subtitle Text Color</span>}>
                    <Input
                      type="color"
                      style={{ width: "100%", height: 42, cursor: "pointer", padding: "2px 6px", borderRadius: 8 }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item name="btnBg" label={<span style={{ fontWeight: 600 }}>Button Accent Color</span>}>
                    <Input
                      type="color"
                      style={{ width: "100%", height: 42, cursor: "pointer", padding: "2px 6px", borderRadius: 8 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* 4. Featured Image */}
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                  <PictureOutlined style={{ color: "#9333EA" }} /> 4. Featured Image / Illustration
                </div>
              }
              style={{ borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 20 }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Radio.Group
                value={inputType}
                onChange={(e) => setInputType(e.target.value)}
                style={{ marginBottom: 14 }}
              >
                <Radio.Button value="upload">Upload Image File</Radio.Button>
                <Radio.Button value="url">Direct Image URL</Radio.Button>
              </Radio.Group>

              {inputType === "upload" ? (
                <Upload
                  beforeUpload={handleImageUpload}
                  showUploadList={false}
                  accept="image/*"
                  disabled={isUploadingImage}
                >
                  <Button icon={<UploadOutlined />} loading={isUploadingImage} size="large" style={{ width: "100%", borderRadius: 8 }}>
                    {isUploadingImage ? "Uploading Image to Server..." : "Select & Upload Image"}
                  </Button>
                </Upload>
              ) : (
                <Form.Item name="imageUrl" style={{ marginBottom: 0 }}>
                  <Input
                    placeholder="https://images.unsplash.com/..."
                    size="large"
                    style={{ borderRadius: 8 }}
                    onChange={(e) => {
                      setImagePreview(e.target.value);
                      setPreviewValues(prev => ({ ...prev, imageUrl: e.target.value }));
                    }}
                  />
                </Form.Item>
              )}

              {imageError && (
                <div style={{ color: "#ef4444", fontSize: 12, marginTop: 6 }}>{imageError}</div>
              )}
            </Card>

            {/* 5. Status & Visibility */}
            <Card
              style={{ borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 24 }}
              bodyStyle={{ padding: "16px 20px" }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <span style={{ fontWeight: 700, color: "#1e293b", fontSize: 14 }}>Active Status</span>
                  <div style={{ fontSize: 12, color: "#64748b" }}>Show this collection on the Customer App & Website home screen</div>
                </div>
                <Form.Item name="isActive" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </div>
            </Card>

            {/* Bottom Form Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginBottom: 40 }}>
              <Button
                size="large"
                onClick={() => navigate("/admin/lifestyles")}
                style={{ borderRadius: 8, padding: "0 24px" }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                style={{
                  background: "linear-gradient(135deg, #0055FF 0%, #0040C0 100%)",
                  borderColor: "#0055FF",
                  fontWeight: 700,
                  padding: "0 32px",
                  borderRadius: 8
                }}
              >
                {isEditing ? "Update Collection" : "Save Collection"}
              </Button>
            </div>
          </Col>

          {/* Right Column: Sticky Live Card Simulation */}
          <Col xs={24} lg={9}>
            <div style={{
              position: "sticky",
              top: 20,
              background: "#ffffff",
              padding: 20,
              borderRadius: 14,
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 16px rgba(0,0,0,0.06)"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14, color: "#475569", marginBottom: 14 }}>
                <EyeOutlined style={{ color: "#0055FF" }} /> Live Customer View Simulation
              </div>

              {/* Live Rendered Card Preview */}
              <div
                style={{
                  background: previewValues.bg || "#EFF6FF",
                  borderRadius: 16,
                  border: `2px solid ${previewValues.borderColor || "#DBEAFE"}`,
                  padding: 18,
                  position: "relative",
                  overflow: "hidden",
                  minHeight: 200,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.07)",
                  transition: "all 0.3s ease"
                }}
              >
                {/* Section Badge */}
                <div style={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}>
                  <Tag
                    color={
                      previewValues.type === "lifestyle" ? "purple" :
                        previewValues.type === "need" ? "green" : "orange"
                    }
                    style={{ fontWeight: 800, fontSize: 10, textTransform: "uppercase", borderRadius: 12 }}
                  >
                    {previewValues.type === "lifestyle" ? "✨ Lifestyle" :
                      previewValues.type === "need" ? "🎯 Need" : "🔥 Both"}
                  </Tag>
                </div>

                {/* Card Title & Subtitle */}
                <div style={{ zIndex: 2, maxWidth: "60%" }}>
                  <h4
                    style={{
                      fontSize: 17,
                      fontWeight: 800,
                      color: previewValues.titleColor || "#003366",
                      margin: "0 0 6px 0",
                      lineHeight: 1.2,
                      whiteSpace: "pre-line"
                    }}
                  >
                    {previewValues.title || "Collection Title"}
                  </h4>
                  <p
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: previewValues.subColor || "#336699",
                      margin: 0,
                      lineHeight: 1.3,
                      whiteSpace: "pre-line"
                    }}
                  >
                    {previewValues.subtitle || "Subtitle tagline"}
                  </p>
                </div>

                {/* Card Action Button */}
                <div style={{ zIndex: 2, marginTop: 24 }}>
                  <button
                    type="button"
                    style={{
                      background: previewValues.btnBg || "#0055FF",
                      color: "#fff",
                      border: "none",
                      borderRadius: 20,
                      padding: "6px 16px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "default",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                      display: "flex",
                      alignItems: "center",
                      gap: 4
                    }}
                  >
                    Explore &gt;
                  </button>
                </div>

                {/* Floating Image Thumbnail */}
                <div
                  style={{
                    position: "absolute",
                    right: 10,
                    bottom: 10,
                    width: 110,
                    height: 110,
                    borderRadius: 14,
                    overflow: "hidden",
                    background: "rgba(255,255,255,0.85)",
                    border: "1.5px solid rgba(255,255,255,0.9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                  }}
                >
                  {previewValues.imageUrl ? (
                    <SafeImage
                      src={getImageUrl(previewValues.imageUrl)}
                      alt="preview"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <PictureOutlined style={{ fontSize: 36, color: "#94a3b8" }} />
                  )}
                </div>
              </div>

              {/* Palette Tokens */}
              <div style={{ marginTop: 18, background: "#f8fafc", padding: 14, borderRadius: 10, border: "1px solid #e2e8f0" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 10 }}>
                  Active Color Tokens
                </span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 11 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 4, background: previewValues.bg, border: "1px solid #cbd5e1" }} />
                    <span style={{ color: "#334155", fontWeight: 600 }}>BG: {previewValues.bg}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 4, background: previewValues.titleColor, border: "1px solid #cbd5e1" }} />
                    <span style={{ color: "#334155", fontWeight: 600 }}>Title: {previewValues.titleColor}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 4, background: previewValues.btnBg, border: "1px solid #cbd5e1" }} />
                    <span style={{ color: "#334155", fontWeight: 600 }}>Btn: {previewValues.btnBg}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 4, background: previewValues.borderColor, border: "1px solid #cbd5e1" }} />
                    <span style={{ color: "#334155", fontWeight: 600 }}>Border: {previewValues.borderColor}</span>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 14, fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
                Changes update in real-time on App and Web
              </div>
            </div>
          </Col>
        </Row>
      </Form>

      {/* Dedicated Category-Filtered Product Picker Modal */}
      <CuratedProductPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedProductIds={selectedProductIds}
        linkedCategoryIds={watchedCategories}
        categories={categories}
        onSave={handleSavePickedProducts}
      />
    </div>
  );
};

export default LifestyleForm;
